import type { EstadoVigencia } from '@/features/proveedores/api/supplierProfile.service'
import { Badge, type ColorDeBadge } from '@/shared/components/Badge'

// Badge de estado de vigencia de un documento.
//
// El valor bueno lo calcula el backend (J-02 / SP-226) y lo entrega en
// `estadoVigencia`. Llega vacío solo mientras el job diario no ha visto el
// documento; ahí se deriva de la fecha que capturó el proveedor, que ya es un
// dato real. Sin fecha => "Sin fecha".
const CONFIG: Record<EstadoVigencia, { label: string; color: ColorDeBadge }> = {
  vigente: { label: 'Vigente', color: 'verde' },
  por_vencer: { label: 'Por vencer', color: 'azul' },
  vencido: { label: 'Vencido', color: 'rojo' },
  sin_fecha: { label: 'Sin fecha', color: 'gris' },
}

interface VigenciaBadgeProps {
  estado?: EstadoVigencia | null
  /**
   * Fecha capturada por el proveedor. Se usa solo si el back todavía no manda
   * `estadoVigencia` para ese documento: sin esto, un documento con fecha se
   * pintaba igual que uno sin ella, lo cual es sencillamente falso.
   *
   * Aquí no se decide qué es "por vencer": ese umbral es una regla de negocio
   * que define Contraloría y calcula el back (J-02 / SP-226). En este respaldo
   * solo se distingue lo que la fecha dice sin interpretar: vigente o vencido.
   */
  validUntil?: string | null
  className?: string
}

const derivarDeFecha = (validUntil?: string | null): EstadoVigencia => {
  if (!validUntil) return 'sin_fecha'
  const soloFecha = /^\d{4}-\d{2}-\d{2}/.exec(validUntil)
  if (!soloFecha) return 'sin_fecha'
  // Se comparan cadenas YYYY-MM-DD: no hay conversión a Date, así que no hay
  // corrimiento de un día por zona horaria.
  const hoy = new Date()
  const hoyISO = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`
  return soloFecha[0] < hoyISO ? 'vencido' : 'vigente'
}

export const VigenciaBadge = ({ estado, validUntil, className = '' }: VigenciaBadgeProps) => {
  const cfg = CONFIG[estado ?? derivarDeFecha(validUntil)]
  return (
    <Badge color={cfg.color} className={className}>
      {cfg.label}
    </Badge>
  )
}

export default VigenciaBadge
